import replace from 'lodash/replace';
import mapKeys from 'lodash/mapKeys';
import snakeCase from 'lodash/snakeCase';
import camelCase from 'lodash/camelCase';
import findKey from 'lodash/findKey';
import find from 'lodash/find';
import omit from 'lodash/omit';
import server from 'common/utils/server';
import { temporaryHashId } from 'common/utils/temporaryHashId';
import { notifyError } from 'common/state/notificationState';
import { loadCoachingSessionHistory } from '../coachingSessionLogState';
import {
  createFileSessionAttachment,
  formatMetricAttachment,
  formatSolicitationAttachment,
  formatQaReviewAttachment,
  formatFileAttachment,
  formatUrlAttachment
} from '../coachingSessionAttachments';
import { DateTime } from 'common/utils/date';

export const NOTES_ON_CHANGE = 'NOTES_ON_CHANGE';
export const TITLE_ON_CHANGE = 'TITLE_ON_CHANGE';
export const START_UPLOADING = 'START_UPLOADING';
export const FINISH_UPLOADING = 'FINISH_UPLOADING';
export const START_COACHING_SESSION_REQUEST = 'START_COACHING_SESSION_REQUEST';
export const COACHING_SESSION_LOAD_FAILED = 'COACHING_SESSION_LOAD_FAILED';
export const FINISH_COACHING_SESSION_REQUEST =
  'FINISH_COACHING_SESSION_REQUEST';
export const COACHING_SESSION_FILE_DELETE_ERROR =
  'COACHING_SESSION_FILE_DELETE_ERROR';
export const COACHING_SESSION_FILE_DELETE_SUCCESS =
  'COACHING_SESSION_FILE_DELETE_SUCCESS';
export const COACHING_SESSION_FILE_SAVE_SUCCESS =
  'COACHING_SESSION_FILE_SAVE_SUCCESS';
export const COACHING_SESSION_ATTACH_FILE = 'COACHING_SESSION_ATTACH_FILE';
export const COACHING_SESSION_ATTACHMENT_ERROR =
  'COACHING_SESSION_ATTACHMENT_ERROR';
export const COACHING_SESSION_PRIVATE_SESSION =
  'COACHING_SESSION_PRIVATE_SESSION';
export const COACHING_SESSION_SAVE_SUCCESS = 'COACHING_SESSION_SAVE_SUCCESS';
export const RESTORE_TAKEAWAY = 'RESTORE_TAKEAWAY';
export const ADD_TAKEAWAY = 'ADD_TAKEAWAY';
export const REMOVE_TAKEAWAY = 'REMOVE_TAKEAWAY';
export const SET_TAKEAWAY = 'SET_TAKEAWAY';
export const HIDE_CREATE_TAKEAWAY_FORM = 'HIDE_CREATE_TAKEAWAY_FORM';
export const TOGGLE_COACHING_MODE = 'TOGGLE_COACHING_MODE';
export const refreshLessons = {
  PENDING: 'coaching_session/REFRESH_LESSONLY_LESSONS/PENDING',
  DONE: 'coaching_session/REFRESH_LESSONLY_LESSONS/DONE',
};

export function notesOnChange(value) {
  return { type: NOTES_ON_CHANGE, value };
}

export function titleOnChange(value) {
  return { type: TITLE_ON_CHANGE, value };
}

export function startCoachingSessionRequest() {
  return { type: START_COACHING_SESSION_REQUEST };
}

export function finishCoachingSessionRequest() {
  return { type: FINISH_COACHING_SESSION_REQUEST };
}

export function startUploading() {
  return { type: START_UPLOADING };
}

export function finishUploading() {
  return { type: FINISH_UPLOADING };
}

function coachingSessionLoadFailed() {
  return { type: COACHING_SESSION_LOAD_FAILED };
}

function coachingSessionFileDeleteError(coachingSession) {
  return { type: COACHING_SESSION_FILE_DELETE_ERROR, coachingSession };
}

function coachingSessionFileDeleteSuccess(fileId) {
  return { type: COACHING_SESSION_FILE_DELETE_SUCCESS, fileId };
}

function coachingSessionAttachmentError() {
  return { type: COACHING_SESSION_ATTACHMENT_ERROR };
}

export function coachingSessionAttachFile(file) {
  return { type: COACHING_SESSION_ATTACH_FILE, file };
}

function coachingSessionAttachFileComplete(file, temporaryFileId) {
  return { type: COACHING_SESSION_FILE_SAVE_SUCCESS, file, temporaryFileId };
}

function coachingSessionSaveSuccess({ updatedAt, sharedAt }) {
  return { type: COACHING_SESSION_SAVE_SUCCESS, updatedAt, sharedAt };
}

export function coachingSessionPrivateSession(isPrivate) {
  return { type: COACHING_SESSION_PRIVATE_SESSION, value: isPrivate };
}

export function restoreTakeaway(takeaway) {
  return { type: RESTORE_TAKEAWAY, takeaway };
}

export function addTakeaway(takeaway, status) {
  return { type: ADD_TAKEAWAY, takeaway, status };
}

export function removeTakeaway(takeaway, status) {
  return { type: REMOVE_TAKEAWAY, takeaway, status };
}

export function setTakeaway(takeawayToUpdate) {
  return { type: SET_TAKEAWAY, takeawayToUpdate };
}

export function setEditMode(takeaway) {
  const takeawayToUpdate = { ...takeaway, isEditing: true };
  return dispatch => {
    dispatch(setTakeaway(takeawayToUpdate));
    dispatch(addTakeaway(takeaway, 'editingTakeaways'));
  };
}

export const cancelEdit = takeaway => dispatch => {
  if (takeaway.isEditing) {
    const takeawayToUpdate = { ...takeaway, isEditing: false };
    dispatch(setTakeaway(takeawayToUpdate));
    dispatch(restoreTakeaway(takeaway));
  } else {
    dispatch({ type: HIDE_CREATE_TAKEAWAY_FORM });
  }
};

export function setTakeawayText(takeaway, value) {
  const takeawayToUpdate = { ...takeaway, text: value };
  return dispatch => dispatch(setTakeaway(takeawayToUpdate));
}

export function setCategory(takeaway, value) {
  const takeawayToUpdate = { ...takeaway, takeawayCategoryId: value };
  return dispatch => dispatch(setTakeaway(takeawayToUpdate));
}

export function setTakeawayCompletedAt(takeaway, value) {
  const takeawayToUpdate = { ...takeaway, completedAt: value };

  return dispatch => dispatch(setTakeaway(takeawayToUpdate));
}

export function showCreateTakeawayForm(coachingSessionId) {
  const blankTakeaway = {
    id: null,
    isPositive: false,
    text: null,
    categoryId: null,
    completedAt: null,
    coachingSessionId,
  };

  return dispatch => {
    dispatch(addTakeaway(blankTakeaway, 'takeawaysToAdd'));
  };
}

function takeawayFormError(takeaway) {
  const takeawayToUpdate = { ...takeaway, error: true };
  return dispatch => dispatch(setTakeaway(takeawayToUpdate));
}

function takeawayUpdateSuccess(takeaway) {
  const updates = { isLoading: false, error: false, isEditing: false };
  const takeawayToUpdate = { ...takeaway, ...updates };
  return dispatch => {
    dispatch(setTakeaway(takeawayToUpdate));
    dispatch(removeTakeaway(takeawayToUpdate, 'editingTakeaways'));
  };
}

function takeawayCreateSuccess(takeaway) {
  return dispatch => {
    dispatch(addTakeaway(takeaway, 'current'));
    dispatch(addTakeaway(takeaway, 'allOpen'));
    dispatch(removeTakeaway({ id: null }, 'takeawaysToAdd'));
  };
}

// Thunk Middleware

export function addKudo({ text }) {
  return (dispatch, getState) => {
    const { id } = getState().coachingSession.session;
    dispatch(startCoachingSessionRequest());
    server
      .post('/api/v1/coaching/kudo', { coaching_session_id: id, text })
      .then(({ kudo }) => {
        dispatch({
          type: 'COACHING_SESSION/created_kudo',
          kudo,
        });
        dispatch(finishCoachingSessionRequest());
      });
  };
}

export function deleteKudo(kudoId) {
  return dispatch => {
    dispatch(startCoachingSessionRequest());
    dispatch({
      type: 'COACHING_SESSION/deleted_kudo',
      kudoId,
    });
    return server
      .delete(`/api/v1/coaching/kudo/${kudoId}`)
      .then(() => dispatch(finishCoachingSessionRequest()));
  };
}

export function updateTakeaway(takeaway) {
  const takeawayRequestBody = mapKeys(takeaway, (_, key) => snakeCase(key));

  return function fetchData(dispatch) {
    dispatch(startCoachingSessionRequest());
    dispatch(setTakeaway(takeaway));
    return server
      .put(`/api/v1/coaching/takeaway/${takeaway.id}`, takeawayRequestBody)
      .then(json => {
        const takeawayResponse = mapKeys(json, (_, key) => camelCase(key));

        dispatch(takeawayUpdateSuccess(takeawayResponse));
        dispatch(finishCoachingSessionRequest());
      })
      .catch(() => {
        dispatch(takeawayFormError(takeaway));
      });
  };
}

export function updateKudoText({ kudoId, text }) {
  return function doUpdate(dispatch) {
    dispatch(startCoachingSessionRequest());
    dispatch({
      type: 'coaching_session/SET_KUDO_TEXT',
      kudoId,
      text,
    });
    return server
      .put(`/api/v1/coaching/kudo/${kudoId}`, { text })
      .then(({ kudo }) => {
        dispatch({ type: 'coaching_session/SET_KUDO', kudo });
        dispatch(finishCoachingSessionRequest());
      });
  };
}

export function createTakeaway(takeaway) {
  const takeawayRequestBody = mapKeys(takeaway, (_, key) => snakeCase(key));

  return function fetchData(dispatch) {
    dispatch(startCoachingSessionRequest());
    dispatch(setTakeaway(takeaway));
    return server
      .post('/api/v1/coaching/takeaway', takeawayRequestBody)
      .then(json => {
        const takeawayResponse = mapKeys(json, (_, key) => camelCase(key));

        dispatch(takeawayCreateSuccess(takeawayResponse));
        dispatch(finishCoachingSessionRequest());
      })
      .catch(() => {
        dispatch(takeawayFormError(takeaway));
      });
  };
}

export function deleteTakeaway(takeaway, status) {
  return dispatch => {
    dispatch(startCoachingSessionRequest());
    dispatch(removeTakeaway(takeaway, status));
    dispatch(removeTakeaway(takeaway, 'allOpen'));
    return server
      .delete(`/api/v1/coaching/takeaway/${takeaway.id}`)
      .then(() => dispatch(finishCoachingSessionRequest()))
      .catch(e => {
        dispatch(takeawayFormError(e));
      });
  };
}

function coachingSessionUpdate(andPerformShare = false) {
  return (dispatch, getState) => {
    const { session, kudos } = getState().coachingSession;
    const params = {
      active: session.active,
      coach_id: session.coachId,
      employee_id: session.employeeId,
      id: session.id,
      notes: session.notes,
      private: session.private,
      shared_at: session.sharedAt,
      title: session.title,
      updated_at: session.updatedAt,
      kudos: kudos.map(k => ({
        id: k.id,
        text: k.text,
      })),
    };
    if (andPerformShare) params.shared_at = true; // TODO: make what this is doing explicit.

    dispatch(startCoachingSessionRequest());
    return server
      .put(`/api/v1/coaching/coaching_session/${session.id}`, params)
      .then(({ coachingSession }) => {
        const { session } = coachingSession;
        dispatch(
          coachingSessionSaveSuccess({
            updatedAt: session.updatedAt,
            sharedAt: session.sharedAt,
          })
        );
        dispatch(finishCoachingSessionRequest());
      });
  };
}

function uploadSessionFile(sessionId, file) {
  return dispatch => {
    dispatch(startUploading());
    dispatch(startCoachingSessionRequest());
    const formData = new FormData();
    formData.append('attachment', file);

    return server
      .post(
        `/api/v1/coaching/coaching_session/${sessionId}/coaching_files`,
        formData
      )
      .then(({ id, url }) => {
        dispatch(finishCoachingSessionRequest());
        return { id, url };
      })
      .catch(error => {
        dispatch({
          type: 'COACHING_SESSION_FILE_SAVE_ERROR',
          error,
        });
      }).finally(() => {
        dispatch(finishUploading());
      });
  };
}

export function deleteSessionFile(fileId, coachingSessionId) {
  return dispatch =>
    server
      .delete(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/coaching_files/${fileId}`
      )
      .then(() => dispatch(coachingSessionFileDeleteSuccess(fileId)))
      .catch(error => dispatch(coachingSessionFileDeleteError(error)));
}

export function createUrlAttachment(coachingSessionId, url, name) {
  return dispatch => {
    dispatch(startCoachingSessionRequest());
    return server
      .post(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/url_attachments`,
        { url, name }
      )
      .then(response =>
        dispatch({
          type: 'CREATE_SESSION_ATTACHMENT',
          attachmentListName: 'urlAttachments',
          entity: {
            id: response.id,
            url: response.url,
            name: response.name,
            createdAt: response.created_at,
          },
        })
      );
  };
}

export function createCoachingSessionDraft({ employeeId }) {
  return dispatch => {
    dispatch(startCoachingSessionRequest());
    return server
      .get(`/api/v1/coaching/coaching_session/new?employee_id=${employeeId}`)

      .then(({ coachingSession }) => {
        return dispatch(loadCoachingSessionHistory({ employeeId })).then(() => {
          dispatch(finishCoachingSessionRequest());
          return { id: coachingSession.session.id };
        });
      });
  };
}

export const sanitizeNotes = notes => replace((notes || ''),  /<!--block-->/g, '');

export const formatPublicNote = publicNote => ({
  id: publicNote.id,
  coachingSessionId: publicNote.coaching_session_id,
  createdAt: publicNote.created_at,
  createdById: publicNote.created_by_id,
  notes: sanitizeNotes(publicNote.notes),
});

export const formatPrivateNote = privateNote => ({
  id: privateNote.id,
  coachingSessionId: privateNote.coaching_session_id,
  createdAt: privateNote.created_at,
  createdById: privateNote.created_by_id,
  notes: sanitizeNotes(privateNote.notes),
});

export function loadCoachingSession({ sessionId }) {
  return dispatch => {
    dispatch({ type: 'COACHING_SESSION/set_loaded', loaded: false });
    dispatch(startCoachingSessionRequest());
    return server
      .get(`/api/v1/coaching/coaching_session/${sessionId}`)
      .then(resp => {
        const {
          coachingSession,
          publicNotes: publicNotesJson,
          privateNote: privateNoteJson,
          metricAttachments: metricAttachmentsJson,
          solicitationAttachments: solicitationAttachmentsJson,
          qaReviewAttachments: qaReviewAttachmentsJson,
          fileAttachments: fileAttachmentsJson,
          urlAttachments: urlAttachmentsJson,
        } = resp;
        dispatch({
          type: 'LOADED_COACHING_SESSION',
          coachingSession,
          publicNotes: publicNotesJson.map(formatPublicNote),
          privateNote: formatPrivateNote(privateNoteJson),
          metricAttachments: metricAttachmentsJson.map(formatMetricAttachment),
          solicitationAttachments: solicitationAttachmentsJson.map(
            formatSolicitationAttachment
          ),
          qaReviewAttachments: qaReviewAttachmentsJson.map(
            formatQaReviewAttachment
          ),
          fileAttachments: fileAttachmentsJson.map(formatFileAttachment),
          urlAttachments: urlAttachmentsJson.map(formatUrlAttachment),
        });
        dispatch(finishCoachingSessionRequest());
      })
      .catch(() => dispatch(coachingSessionLoadFailed()));
  };
}

export function refreshLessonlyLessons({ sessionId }) {
  return dispatch => {
    dispatch({
      type: refreshLessons.PENDING,
    });
    dispatch(startCoachingSessionRequest());
    return server
      .get(`/api/v1/coaching/coaching_session/${sessionId}`)
      .then(resp => {
        const lessonlyLessons = resp?.coachingSession.lessonlyLessons;

        dispatch({
          type: refreshLessons.DONE,
          lessonlyLessons,
        });
        dispatch(finishCoachingSessionRequest());
      })
      .catch(() => dispatch(coachingSessionLoadFailed()));
  };
}

export function saveCoachingSession() {
  return dispatch => {
    dispatch(startCoachingSessionRequest());
    return dispatch(coachingSessionUpdate()).then(() => {
      dispatch(finishCoachingSessionRequest());
    });
  };
}

export function shareCoachingSession() {
  return dispatch => {
    dispatch(startCoachingSessionRequest());
    return dispatch(coachingSessionUpdate(true)).then(() => {
      dispatch({
        type: 'COACHING_SESSION/toggle_success_modal',
        value: true,
      });
      dispatch(finishCoachingSessionRequest());
    });
  };
}

export function reshareCoachingSession(sessionId) {
  return dispatch => {
    dispatch(startCoachingSessionRequest());
    return server
      .get(`/api/v1/coaching/coaching_session/${sessionId}/reshare`)
      .then(({ shared_at: sharedAt }) => {
        dispatch({ type: 'COACHING_SESSION/reshared', sharedAt });
        dispatch(finishCoachingSessionRequest());
      });
  };
}

export function coachingSessionFile(file, sessionId) {
  return (dispatch, getState) => {
    const FILE_SIZE_LIMIT = 25 * 1024 * 1024;
    const fileNames = getState().coachingSession.files.map(f => f.name);
    if (fileNames.includes(file.name)) {
      return dispatch(notifyError('File with that name already attached'));
    }

    if (file.size > FILE_SIZE_LIMIT) {
      return dispatch(coachingSessionAttachmentError());
    }

    const temporaryFileId = temporaryHashId();
    const temporaryAttachmentId = temporaryHashId();
    dispatch(
      coachingSessionAttachFile({ id: temporaryFileId, name: file.name })
    );
    return dispatch(uploadSessionFile(sessionId, file)).then(({ id, url }) => {
      dispatch(coachingSessionAttachFileComplete({ id, url }, temporaryFileId));
      return dispatch(
        createFileSessionAttachment(sessionId, id, temporaryAttachmentId)
      );
    });
  };
}

export function getFallbackSessionId(state) {
  let mostRecentDraft = state.coachingSessionLogState.drafts[0] || null;
  state.coachingSessionLogState.drafts.forEach(draft => {
    if (
      DateTime.fromISO(draft.updatedAt) >
      DateTime.fromISO(mostRecentDraft.updatedAt)
    ) {
      mostRecentDraft = draft;
    }
  });
  let mostRecentSession = state.coachingSessionLogState.sessions[0] || null;
  state.coachingSessionLogState.sessions.forEach(session => {
    if (
      DateTime.fromISO(session.sharedAt) >
      DateTime.fromISO(mostRecentSession.sharedAt)
    ) {
      mostRecentSession = session;
    }
  });
  const fallbackSessionId =
    (mostRecentDraft ? mostRecentDraft.id : null) ||
    (mostRecentSession ? mostRecentSession.id : null) ||
    null;
  return fallbackSessionId;
}

export const deleteCoachingSession =
  ({ sessionId }) =>
  (dispatch, getState) => {
    const employeeId = getState().coachingSession.employee.id;
    return server
      .delete(`/api/v1/coaching/coaching_session/${sessionId}`)
      .then(() => dispatch(loadCoachingSessionHistory({ employeeId })));
  };

function statusForTakeaway(state, takeaway) {
  const displayStates = omit(state.takeaways, 'editingTakeaways');
  return findKey(displayStates, takeawaysForStatus =>
    find(takeawaysForStatus, { id: takeaway.id })
  );
}

const initialState = {
  loadingLessons: false,
  loaded: false,
  loadFailed: false,
  requestsInFlight: 0,
  historyPage: 1,
  coach: {},
  employee: {},
  session: {},
  availableCategories: [],
  isLoading: false,
  isCoachingMode: false,
  isUploading: false,
  files: [],
  attachmentErrorMessage: null,
  sharedSessionModalToggle: false,
  takeaways: {
    current: [],
    allOpen: [],
    takeawaysToAdd: [],
    editingTakeaways: [],
  },
  kudos: [],
  draftIsDirty: false,
};

export const coachingSession = (state = initialState, action) => {
  switch (action.type) {
    case 'COACHING_SESSION/reset_state': {
      const { requestsInFlight } = state;
      return { ...initialState, requestsInFlight };
    }
    case 'COACHING_SESSION/set_history_page': {
      return { ...state, historyPage: action.historyPage };
    }
    case 'COACHING_SESSION/reshared': {
      const { sharedAt } = action;
      return {
        ...state,
        session: {
          ...state.session,
          sharedAt,
        },
      };
    }
    case 'COACHING_SESSION/recap': {
      const { resharedAt } = action;
      return {
        ...state,
        session: {
          ...state.session,
          resharedAt,
        },
      };
    }
    case 'COACHING_SESSION/created_kudo': {
      const { kudo } = action;
      return {
        ...state,
        kudos: [kudo, ...state.kudos],
      };
    }
    case 'COACHING_SESSION/deleted_kudo': {
      const { kudoId } = action;
      return {
        ...state,
        kudos: state.kudos.filter(k => k.id !== kudoId),
      };
    }
    case 'COACHING_SESSION/set_loaded': {
      return {
        ...state,
        loaded: action.loaded,
      };
    }
    case 'COACHING_SESSION/determined_draft_is_dirty': {
      return { ...state, draftIsDirty: true };
    }
    case 'COACHING_SESSION/determined_draft_is_clean': {
      return { ...state, draftIsDirty: false };
    }
    case START_COACHING_SESSION_REQUEST: {
      return { ...state, requestsInFlight: state.requestsInFlight + 1 };
    }
    case COACHING_SESSION_LOAD_FAILED: {
      return { ...state, loadFailed: true };
    }
    case FINISH_COACHING_SESSION_REQUEST: {
      return { ...state, requestsInFlight: state.requestsInFlight - 1 };
    }
    case START_UPLOADING: {
      return { ...state, isUploading: true };
    }
    case FINISH_UPLOADING: {
      return { ...state, isUploading: false };
    }
    case 'LOADED_COACHING_SESSION': {
      return {
        ...state,
        ...action.coachingSession,
        loaded: true,
        loadFailed: false,
        takeaways: {
          ...state.takeaways,
          ...action.coachingSession.takeaways,
        },
      };
    }
    case COACHING_SESSION_SAVE_SUCCESS: {
      return {
        ...state,
        session: {
          ...state.session,
          updatedAt: action.updatedAt,
          sharedAt: action.sharedAt,
        },
      };
    }
    case 'COACHING_SESSION_FILE_SAVE_ERROR': {
      return {
        ...state,
        attachmentErrorMessage:
          (action.error && action.error.message) ||
          'Something went wrong when uploading the attached file.',
      };
    }
    case COACHING_SESSION_FILE_DELETE_ERROR: {
      return {
        ...state,
        attachmentErrorMessage:
          'Something went wrong when deleting the attached file.',
      };
    }
    case COACHING_SESSION_ATTACHMENT_ERROR: {
      return {
        ...state,
        attachmentErrorMessage:
          'Your attachment is larger than the maximum allowed size of 25Mb',
      };
    }
    case NOTES_ON_CHANGE: {
      return {
        ...state,
        session: { ...state.session, notes: action.value },
      };
    }
    case TITLE_ON_CHANGE: {
      return {
        ...state,
        session: { ...state.session, title: action.value },
      };
    }
    case TOGGLE_COACHING_MODE: {
      return {
        ...state,
        isCoachingMode: !state.isCoachingMode,
      };
    }
    case COACHING_SESSION_PRIVATE_SESSION: {
      return {
        ...state,
        session: { ...state.session, private: action.value },
      };
    }
    case 'TOGGLE_COACHING_SESSION_PRIVACY': {
      return {
        ...state,
        session: { ...state.session, private: !state.session.private },
      };
    }
    case COACHING_SESSION_ATTACH_FILE: {
      const { file } = action;
      return {
        ...state,
        files: [...state.files, file],
        attachmentErrorMessage: null,
      };
    }
    case COACHING_SESSION_FILE_SAVE_SUCCESS: {
      const { temporaryFileId, file } = action;
      return {
        ...state,
        files: state.files.map(f => {
          return f.id === temporaryFileId ? { ...f, ...file } : f;
        }),
      };
    }
    case COACHING_SESSION_FILE_DELETE_SUCCESS: {
      const { fileId } = action;
      return {
        ...state,
        files: state.files.filter(f => f.id !== fileId),
      };
    }
    case 'COACHING_SESSION/toggle_success_modal': {
      return {
        ...state,
        sharedSessionModalToggle: action.value,
      };
    }
    case HIDE_CREATE_TAKEAWAY_FORM: {
      return {
        ...state,
        takeaways: {
          ...state.takeaways,
          takeawaysToAdd: [],
        },
      };
    }
    case RESTORE_TAKEAWAY: {
      const { takeaway } = action;
      const status = statusForTakeaway(state, takeaway);

      const original = find(state.takeaways.editingTakeaways, {
        id: takeaway.id,
      });
      const newTakeawaysForStatus = state.takeaways[status].map(t => {
        return t.id === takeaway.id ? original : t;
      });
      const newTakeawaysForAllOpen = state.takeaways.allOpen.map(t => {
        return t.id === takeaway.id ? original : t;
      });

      const editingTakeaways = state.takeaways.editingTakeaways.filter(
        t => t.id !== original.id
      );
      return {
        ...state,
        takeaways: {
          ...state.takeaways,
          [status]: newTakeawaysForStatus,
          allOpen: newTakeawaysForAllOpen,
          editingTakeaways,
        },
      };
    }
    case ADD_TAKEAWAY: {
      const { takeaway, status } = action;
      const newTakeawaysForStatus = state.takeaways[status].concat(takeaway);
      return {
        ...state,
        takeaways: {
          ...state.takeaways,
          [status]: newTakeawaysForStatus,
        },
      };
    }
    case REMOVE_TAKEAWAY: {
      const { takeaway, status } = action;
      const newTakeawaysForStatus = state.takeaways[status].filter(
        t => t.id !== takeaway.id
      );
      return {
        ...state,
        takeaways: {
          ...state.takeaways,
          [status]: newTakeawaysForStatus,
        },
      };
    }
    case SET_TAKEAWAY: {
      const { takeawayToUpdate } = action;
      const status = statusForTakeaway(state, takeawayToUpdate);

      const newTakeawaysForStatus = state.takeaways[status].map(t => {
        return t.id === takeawayToUpdate.id ? takeawayToUpdate : t;
      });

      const newTakeawaysForAllOpen = state.takeaways.allOpen.map(t => {
        return t.id === takeawayToUpdate.id ? takeawayToUpdate : t;
      });

      // closed items aren't included in the allOpen collection.
      // If we re-open one, we need to push it into the allOpen collection.
      if (
        status === 'current' &&
        !find(newTakeawaysForAllOpen, { id: takeawayToUpdate.id })
      ) {
        newTakeawaysForAllOpen.push(takeawayToUpdate);
      }

      return {
        ...state,
        takeaways: {
          ...state.takeaways,
          [status]: newTakeawaysForStatus,
          allOpen: newTakeawaysForAllOpen,
        },
      };
    }
    case 'coaching_session/SET_KUDO_TEXT': {
      const { kudoId, text } = action;
      return {
        ...state,
        kudos: state.kudos.map(kudo => {
          if (kudo.id === kudoId) {
            return { ...kudo, text };
          }
          return kudo;
        }),
      };
    }
    case 'coaching_session/SET_KUDO': {
      const { kudo } = action;
      return {
        ...state,
        kudos: state.kudos.map(k => (k.id === kudo.id ? kudo : k)),
      };
    }
    case 'coaching_session/REFRESH_LESSONLY_LESSONS/PENDING':
      return {
        ...state,
        loadingLessons: true,
      };
    case 'coaching_session/REFRESH_LESSONLY_LESSONS/DONE': {
      const { lessonlyLessons } = action;

      return {
        ...state,
        loadingLessons: false,
        lessonlyLessons,
      };
    }

    default: {
      return state;
    }
  }
};
